import "@draft-js-plugins/static-toolbar/lib/plugin.css";
import "@draft-js-plugins/anchor/lib/plugin.css";
import "draft-js/dist/Draft.css";

import { Box, Flex, useTheme } from "@chakra-ui/react";
import createLinkPlugin from "@draft-js-plugins/anchor";
import {
  BoldButton,
  ItalicButton,
  UnderlineButton,
} from "@draft-js-plugins/buttons";
import Editor from "@draft-js-plugins/editor";
import createToolbarPlugin, {
  Separator,
} from "@draft-js-plugins/static-toolbar";
import {
  ContentState,
  convertFromHTML,
  EditorState,
  Modifier,
  RichUtils,
  SelectionState,
} from "draft-js";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FieldPath } from "react-hook-form";

import useCurrentUser from "../../../hooks/useCurrentUser";
import { AiInterviewerFormValues } from "../types";
import { useAiInterviewerFormContext } from "../useAiInterviewerFormContext";
import {
  cloneRegex,
  createWildcardPattern,
  INVITE_URL_LINK_REGEX,
} from "./constants";
import { preprocessHtmlForPaste, remapEntitiesForPaste } from "./contentUtils";
import { editorStateToHtml, htmlToEditorState } from "./htmlConversion";
import {
  cursorIsAtImmutableLinkEnd,
  cursorIsAtImmutableLinkStart,
  hasImmutableLink,
  selectionIncludesImmutableLink,
} from "./linkUtils";
import { createWildcardStrategy, WildcardSpan } from "./WildcardDecorator";
import WildcardsDropdown, { Wildcard } from "./WildcardsDropdown";

export interface EmailBodyEditorProps {
  value: string;
  fieldName: FieldPath<AiInterviewerFormValues>;
  onChange: (html: string) => void;
  availableWildcards?: Wildcard[];
  readOnly?: boolean;
  placeholder?: string;
}

const EmailBodyEditor: React.FC<EmailBodyEditorProps> = ({
  value,
  fieldName,
  onChange,
  availableWildcards = [],
  readOnly = false,
  placeholder = "Enter email body...",
}) => {
  const { colors } = useTheme();
  const currentUser = useCurrentUser();
  const companyName = currentUser.organization.name;
  const [editorIsInFocus, setEditorIsInFocus] = useState(false);
  const { resetField } = useAiInterviewerFormContext();
  const isFirstRenderRef = useRef(true);

  // Store the last valid selection for wildcard insertion
  const lastSelectionRef = useRef<SelectionState | null>(null);

  // Memoize the wildcard regex based on available wildcards
  const wildcardRegex = useMemo(() => {
    if (availableWildcards.length === 0) {
      return null;
    }

    const pattern = createWildcardPattern(
      availableWildcards.map((w) => w.name)
    );
    return new RegExp(pattern, "g");
  }, [availableWildcards]);

  // Memoize the wildcard pattern string for plugin dependency
  const wildcardPattern = useMemo(() => {
    if (availableWildcards.length === 0) {
      return null;
    }
    return createWildcardPattern(availableWildcards.map((w) => w.name));
  }, [availableWildcards]);

  /**
   * Finds all wildcard matches in the given text.
   */
  const findWildcardsInText = useCallback(
    (text: string): Array<{ start: number; end: number }> => {
      if (!wildcardRegex) return [];

      const matches: Array<{ start: number; end: number }> = [];
      const freshRegex = cloneRegex(wildcardRegex);
      let match = freshRegex.exec(text);

      while (match !== null) {
        matches.push({
          start: match.index,
          end: match.index + match[0].length,
        });
        match = freshRegex.exec(text);
      }

      return matches;
    },
    [wildcardRegex]
  );

  // Setup plugins - create a plugin for wildcard decorator so it combines with link plugin decorator
  const [plugins, Toolbar, LinkButton] = useMemo(() => {
    const linkPlugin = createLinkPlugin({
      theme: {
        input: "link-modal-input",
        inputInvalid: "link-modal-input-invalid",
        link: "",
      },
      placeholder: "Enter a valid URL and hit enter",
    });
    const staticToolbarPlugin = createToolbarPlugin({
      theme: {
        buttonStyles: {
          button: "draftjs-button",
          active: "draftjs-button-active",
        },
        toolbarStyles: { toolbar: "draftjs-toolbar" },
      },
    });

    // Create wildcard strategy from factory function using the pattern string
    // Factory handles regex creation and cloning internally
    const wildcardStrategy = createWildcardStrategy(wildcardPattern);

    // Create a plugin for wildcard decorator so it combines with other plugin decorators
    const wildcardPlugin = {
      decorators: [
        {
          strategy: wildcardStrategy,
          component: WildcardSpan,
        },
      ],
    };

    return [
      [linkPlugin, staticToolbarPlugin, wildcardPlugin],
      staticToolbarPlugin.Toolbar,
      linkPlugin.LinkButton,
    ];
  }, [wildcardPattern]);

  // Initialize editor state from HTML value
  // The Editor component will automatically combine decorators from all plugins
  const [editorState, setEditorState] = useState<EditorState>(() => {
    return htmlToEditorState(value, companyName);
  });

  // Update editor state when value prop changes (external updates)
  useEffect(() => {
    const currentHtml = editorStateToHtml(editorState);
    if (value !== currentHtml) {
      const newEditorState = htmlToEditorState(value, companyName);
      setEditorState(newEditorState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Find wildcard that cursor is inside
  const findWildcardAtCursor = useCallback(
    (
      state: EditorState
    ): { start: number; end: number; blockKey: string } | null => {
      const selection = state.getSelection();
      if (!selection.isCollapsed()) {
        return null; // Only handle collapsed selections (cursor position)
      }

      const startOffset = selection.getStartOffset();
      const startKey = selection.getStartKey();
      const block = state.getCurrentContent().getBlockForKey(startKey);
      const wildcards = findWildcardsInText(block.getText());

      const wildcard = wildcards.find(
        (w) => startOffset > w.start && startOffset <= w.end
      );

      return wildcard ? { ...wildcard, blockKey: startKey } : null;
    },
    [findWildcardsInText]
  );

  // Handle key commands to prevent deletion of immutable link and handle wildcard deletion
  const handleKeyCommand = useCallback(
    (command: string, currentState: EditorState): "handled" | "not-handled" => {
      // Check if deletion command
      if (command === "backspace" || command === "delete") {
        if (selectionIncludesImmutableLink(currentState)) {
          // Prevent deletion of immutable link
          return "handled";
        }

        // For backspace, also block if cursor is immediately after the immutable link
        // (half-open interval semantics allow typing there, but backspace would delete into the link)
        if (
          command === "backspace" &&
          cursorIsAtImmutableLinkEnd(currentState)
        ) {
          return "handled";
        }

        // For delete, also block if cursor is at the start of the immutable link
        // (half-open interval semantics allow typing there, but delete would delete into the link)
        if (
          command === "delete" &&
          cursorIsAtImmutableLinkStart(currentState)
        ) {
          return "handled";
        }

        // Check if cursor is inside or right after a wildcard
        const wildcardAtCursor = findWildcardAtCursor(currentState);
        if (wildcardAtCursor) {
          // Delete the entire wildcard
          const selection = SelectionState.createEmpty(
            wildcardAtCursor.blockKey
          ).merge({
            anchorOffset: wildcardAtCursor.start,
            focusOffset: wildcardAtCursor.end,
          });

          const newContentState = Modifier.removeRange(
            currentState.getCurrentContent(),
            selection,
            "backward"
          );
          const newEditorState = EditorState.push(
            currentState,
            newContentState,
            "remove-range"
          );

          // Update selection to be at the position where wildcard was
          const finalSelection = SelectionState.createEmpty(
            wildcardAtCursor.blockKey
          ).merge({
            anchorOffset: wildcardAtCursor.start,
            focusOffset: wildcardAtCursor.start,
          });
          const finalEditorState = EditorState.forceSelection(
            newEditorState,
            finalSelection
          );

          setEditorState(finalEditorState);
          return "handled";
        }
      }

      // Let other commands be handled normally
      const newState = RichUtils.handleKeyCommand(currentState, command);
      if (newState) {
        setEditorState(newState);
        return "handled";
      }
      return "not-handled";
    },
    [findWildcardAtCursor]
  );

  // Check if selection is within a wildcard (but not the entire wildcard)
  const selectionIsWithinWildcard = useCallback(
    (state: EditorState): boolean => {
      const selection = state.getSelection();
      const startKey = selection.getStartKey();
      const startOffset = selection.getStartOffset();
      const endOffset = selection.getEndOffset();
      const block = state.getCurrentContent().getBlockForKey(startKey);
      const wildcards = findWildcardsInText(block.getText());

      return wildcards.some((wildcard) => {
        const { start: wildcardStart, end: wildcardEnd } = wildcard;

        // Check if selection overlaps with this wildcard (excluding edge positions)
        // Edge positions (exactly at wildcardStart or wildcardEnd) should allow typing
        const selectionOverlaps =
          (startOffset > wildcardStart && startOffset < wildcardEnd) ||
          (endOffset > wildcardStart && endOffset < wildcardEnd) ||
          (startOffset <= wildcardStart && endOffset >= wildcardEnd);

        if (!selectionOverlaps) return false;

        // Check if entire wildcard is selected
        const entireWildcardSelected =
          startOffset === wildcardStart && endOffset === wildcardEnd;

        // If selection is within wildcard but not the entire wildcard, prevent editing
        return !entireWildcardSelected;
      });
    },
    [findWildcardsInText]
  );

  // Handle before input to prevent editing immutable link and wildcards
  const handleBeforeInput = useCallback(
    (chars: string, currentState: EditorState): "handled" | "not-handled" => {
      if (selectionIncludesImmutableLink(currentState)) {
        // Prevent any input that would modify the immutable link
        return "handled";
      }

      if (selectionIsWithinWildcard(currentState)) {
        // Prevent editing inside wildcards (but allow deletion of entire wildcard)
        return "handled";
      }

      return "not-handled";
    },
    [selectionIsWithinWildcard]
  );

  // Handle pasted text to strip {{ invite_url }} links from pasted content
  const handlePastedText = useCallback(
    (
      text: string,
      html: string | undefined,
      currentState: EditorState
    ): "handled" | "not-handled" => {
      // Prevent pasting over the immutable link
      if (selectionIncludesImmutableLink(currentState)) {
        return "handled";
      }

      // Prevent pasting inside wildcards
      if (selectionIsWithinWildcard(currentState)) {
        return "handled";
      }

      // If no HTML, let Draft.js handle plain text paste normally
      if (!html) {
        return "not-handled";
      }

      // Strip any <a> tags where href contains {{ invite_url }}, removing entirely
      // (including link text, since the text "Start your interview" is used to identify
      // immutable links and leaving it as orphan text can cause re-conversion bugs)
      const freshRegex = cloneRegex(INVITE_URL_LINK_REGEX);
      const cleanedHtml = html.replace(freshRegex, "");

      // If nothing was stripped, let Draft.js handle normally
      if (cleanedHtml === html) {
        return "not-handled";
      }

      // Preprocess HTML to convert <br> tags to <p> blocks for proper parsing
      const preprocessedHtml = preprocessHtmlForPaste(cleanedHtml);

      // Convert cleaned HTML to Draft.js content and insert
      const blocksFromHTML = convertFromHTML(preprocessedHtml);
      if (
        !blocksFromHTML.contentBlocks ||
        blocksFromHTML.contentBlocks.length === 0
      ) {
        return "handled"; // Nothing to paste after stripping
      }

      const pastedContent = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
      );

      // Remap entities from pasted content to target content
      const { content: targetContent, remappedBlocks } = remapEntitiesForPaste(
        pastedContent,
        currentState.getCurrentContent()
      );

      const newContent = Modifier.replaceWithFragment(
        targetContent,
        currentState.getSelection(),
        remappedBlocks
      );

      const newEditorState = EditorState.push(
        currentState,
        newContent,
        "insert-fragment"
      );

      // If immutable link was deleted by paste, block it
      const originalHadLink = hasImmutableLink(currentState);
      const newHasLink = hasImmutableLink(newEditorState);
      if (originalHadLink && !newHasLink) {
        return "handled";
      }

      setEditorState(newEditorState);

      // Update lastSelectionRef so wildcard insertion works correctly after paste
      const newSelection = newEditorState.getSelection();
      if (newSelection.getHasFocus()) {
        lastSelectionRef.current = newSelection;
      }

      // Trigger onChange with the new content
      const newHtml = editorStateToHtml(newEditorState);
      onChange(newHtml);

      return "handled";
    },
    [onChange, selectionIsWithinWildcard]
  );

  // Handle editor changes
  const handleEditorChange = (newEditorState: EditorState) => {
    // Check if immutable link was deleted and restore it if needed
    const originalHasLink = hasImmutableLink(editorState);
    const newHasLink = hasImmutableLink(newEditorState);

    if (originalHasLink && !newHasLink) {
      // Immutable link was deleted, restore it from the original value
      const originalHtml = editorStateToHtml(editorState);
      const restoredState = htmlToEditorState(originalHtml);
      setEditorState(restoredState);

      // Convert to HTML and call onChange (with restored link)
      const html = editorStateToHtml(restoredState);
      onChange(html);
      return;
    }

    setEditorState(newEditorState);

    // Save the selection if the editor has focus
    const selection = newEditorState.getSelection();
    if (selection.getHasFocus()) {
      lastSelectionRef.current = selection;
    }

    // Convert to HTML and call onChange
    const html = editorStateToHtml(newEditorState);
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      resetField(fieldName, {
        defaultValue: html,
      });
    } else {
      onChange(html);
    }
  };

  return (
    <Flex
      flexDir="column"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      overflow="hidden"
      bg={readOnly ? "gray.50" : "white"}
      sx={{
        // Style for toolbar buttons
        ".draftjs-button": {
          color: `${colors.gray[800]} !important`,
          fill: `${colors.gray[800]} !important`,
          padding: "6px 8px",
          borderRadius: "md",
          cursor: "pointer",
          transition: "all 0.2s",
          _hover: {
            bg: colors.blue[50],
            color: `${colors.blue[600]} !important`,
            fill: `${colors.blue[600]} !important`,
          },
        },
        ".draftjs-button-active": {
          bg: colors.blue[50],
          color: `${colors.blue[600]} !important`,
          fill: `${colors.blue[600]} !important`,
        },
        // Style for links in the editor
        // The anchor plugin renders links as <a> tags
        ".email-body-editor-content a": {
          color: `${colors.blue[600]} !important`,
          textDecoration: "underline !important",
          cursor: "pointer !important",
        },
        ".email-body-editor-content a:hover": {
          borderBottomColor: `${colors.blue[600]} !important`,
        },
        // Style for link modal input
        ".link-modal-input": {
          padding: "14px 15px",
          borderBottomWidth: "1px",
          borderStyle: "solid",
          borderColor: colors.gray[200],
          fontSize: "md",
          outline: "none",
          transition: "all 0.2s",
          width: "100%",
        },
        ".link-modal-input-invalid": {},
      }}
    >
      {!readOnly && (
        <Toolbar>
          {(externalProps) => (
            <Flex
              alignItems="center"
              px="3"
              py="2"
              borderBottomWidth="1px"
              borderColor="gray.200"
              gap="1"
              flexDir="row"
              justifyContent="space-between"
            >
              <Flex alignItems="center" gap="1">
                <BoldButton {...externalProps} />
                <ItalicButton {...externalProps} />
                <UnderlineButton {...externalProps} />
                <Separator />
                <LinkButton {...externalProps} />
              </Flex>
              {availableWildcards.length > 0 && (
                <WildcardsDropdown
                  editorState={editorState}
                  onChange={handleEditorChange}
                  wildcards={availableWildcards}
                  lastSelectionRef={lastSelectionRef}
                />
              )}
            </Flex>
          )}
        </Toolbar>
      )}
      <Box p="3" minH="200px" position="relative">
        {!editorIsInFocus && !editorState.getCurrentContent().hasText() && (
          <Box
            color="gray.400"
            position="absolute"
            pointerEvents="none"
            top="3"
            left="3"
          >
            {placeholder}
          </Box>
        )}
        <Box className="unset-chakra email-body-editor-content">
          <Editor
            editorState={editorState}
            onChange={handleEditorChange}
            handleKeyCommand={handleKeyCommand}
            handleBeforeInput={handleBeforeInput}
            handlePastedText={handlePastedText}
            onFocus={() => setEditorIsInFocus(true)}
            onBlur={() => setEditorIsInFocus(false)}
            plugins={plugins}
            readOnly={readOnly}
            spellCheck
          />
        </Box>
      </Box>
    </Flex>
  );
};

export default EmailBodyEditor;
